/**
 * Named T&C templates that drive the customer-facing terms section of
 * a quote. Stored as rows in the `cr001_quote_terms_template` Dataverse
 * entity; admins manage them through the Quote Terms admin page.
 *
 * Each template carries:
 *   - A stable slug (`cr001_id_slug`) referenced by quote rows. Quotes
 *     store the slug, not the GUID, so a template rebuild / staging-to-
 *     prod copy doesn't orphan existing quote references.
 *   - A body and optional closer — both support `{{path.to.value}}`
 *     placeholders and `{{#if path}}...{{/if}}` conditionals. See
 *     `QUOTE_TEMPLATE_VARIABLES` below for the resolver vocabulary.
 *   - An `isDefault` flag. The service enforces single-default
 *     invariance: setting `isDefault: true` clears the flag on all
 *     other templates server-side.
 *
 * Audit fields (`createdAt` / `updatedAt` / `updatedBy`) are populated
 * from Dataverse's built-in `createdon` / `modifiedon` / `modifiedby`
 * columns; the service never sets them directly.
 */
import type { TermsBodyFormat } from './termsFormat';

export interface QuoteTermsTemplate {
  /** Dataverse GUID (`cr001_quote_terms_templateid`). Server-managed. */
  id: string;
  /**
   * Stable string identifier referenced by `QuotesListItem.termsTemplateId`.
   * Immutable after creation; survives template rebuilds / soft delete.
   * Slug-shaped (`^[a-z0-9][a-z0-9_-]{0,62}$`).
   */
  slug: string;
  /** Display label shown in the admin list and the quote Select. */
  name: string;
  /** Optional admin memo — never rendered to the customer. */
  description?: string;
  /** Template body. Supports `{{var}}` placeholders + `{{#if var}}...{{/if}}`. */
  body: string;
  /** Optional friendly closer rendered as a separate paragraph below the body. */
  closer?: string;
  /**
   * How `body` and `closer` render — see {@link TermsBodyFormat}. Null in
   * Dataverse (`cr001_body_format`) on every row written before markdown
   * support, and read as `'text'`, so no existing quote changes appearance.
   */
  bodyFormat: TermsBodyFormat;
  /** Exactly one template has this true; enforced server-side. */
  isDefault: boolean;
  /** Dataverse `createdon` (ISO timestamp). */
  createdAt: string;
  /** Dataverse `modifiedon` (ISO timestamp). */
  updatedAt: string;
  /** Dataverse `modifiedby` UPN; null when not resolvable. */
  updatedBy: string | null;
}

/** Body for POST `/api/quote-terms-templates`. */
export interface QuoteTermsTemplateCreate {
  slug: string;
  name: string;
  description?: string;
  body: string;
  closer?: string;
  /** Omitted defaults to `'markdown'` — a body written today knows the subset exists. */
  bodyFormat?: TermsBodyFormat;
  isDefault?: boolean;
}

/** Body for PATCH `/api/quote-terms-templates/{id}`. All fields optional. */
export interface QuoteTermsTemplateUpdate {
  name?: string;
  description?: string;
  body?: string;
  closer?: string;
  bodyFormat?: TermsBodyFormat;
  isDefault?: boolean;
}

export interface QuoteTermsTemplatesListResponse {
  items: QuoteTermsTemplate[];
}

/**
 * Single template variable usable inside a template body.
 *
 * The frontend renders this list as the "Available variables" panel in
 * the editor; backend services can use the same list to flag unknown
 * placeholders in submitted bodies (forward-compat: unknowns warn, not
 * error).
 */
export interface QuoteTemplateVariable {
  /**
   * Dot-path referenced inside `{{ ... }}`. Examples:
   *   - `businessHoursStart`
   *   - `quote.number`
   *   - `customer.businessName`
   */
  path: string;
  /** Human-friendly label rendered in the editor help panel. */
  label: string;
  /** Realistic example of the formatted output (for the help panel). */
  example: string;
  /** Where the value originates — drives grouping in the help panel. */
  source: 'appSettings' | 'quote' | 'customer' | 'system';
}

/**
 * Canonical registry of variables exposed to quote-template bodies.
 *
 * Variables are dispatched through formatters in the frontend renderer
 * (`utils/quoteTemplateRenderer.ts`) so the customer sees "9:00 AM"
 * instead of raw `9`, "Mon–Fri" instead of `[1,2,3,4,5]`, etc.
 *
 * To add a new variable: add an entry here, then implement the matching
 * resolver/formatter in `quoteTemplateRenderer.ts`. Both ends must stay
 * in sync; the registry is the source of truth for what's available.
 */
export const QUOTE_TEMPLATE_VARIABLES: readonly QuoteTemplateVariable[] = [
  // appSettings
  {
    path: 'businessHoursStart',
    label: 'Business hours start',
    example: '9:00 AM',
    source: 'appSettings',
  },
  {
    path: 'businessHoursEnd',
    label: 'Business hours end',
    example: '5:00 PM',
    source: 'appSettings',
  },
  {
    path: 'shoulderHoursStart',
    label: 'Shoulder hours start',
    example: '7:00 AM',
    source: 'appSettings',
  },
  {
    path: 'shoulderHoursEnd',
    label: 'Shoulder hours end',
    example: '6:00 PM',
    source: 'appSettings',
  },
  {
    path: 'workDays',
    label: 'Work days (compact)',
    example: 'Mon–Fri',
    source: 'appSettings',
  },
  {
    path: 'workDaysLong',
    label: 'Work days (long form)',
    example: 'Monday to Friday',
    source: 'appSettings',
  },
  {
    path: 'timezone',
    label: 'Timezone',
    example: 'Australia/Sydney',
    source: 'appSettings',
  },
  {
    path: 'returnGraceDays',
    label: 'Return grace period (days)',
    example: '14',
    source: 'appSettings',
  },

  // quote
  {
    path: 'quote.number',
    label: 'Quote number',
    example: 'Q-2026-0042',
    source: 'quote',
  },
  {
    path: 'quote.validUntil',
    label: 'Quote valid until',
    example: '19 June 2026',
    source: 'quote',
  },
  {
    path: 'quote.total',
    label: 'Quote total (as shown to the customer)',
    example: '$1,234.56',
    source: 'quote',
  },
  {
    path: 'quote.priceDisplayMode',
    label: 'Quote price display mode',
    example: 'ex GST',
    source: 'quote',
  },

  // customer
  {
    path: 'customer.name',
    label: 'Customer (best name)',
    example: 'Acme Pty Ltd',
    source: 'customer',
  },
  {
    path: 'customer.businessName',
    label: 'Customer business name',
    example: 'Acme Pty Ltd',
    source: 'customer',
  },
  {
    path: 'customer.fullName',
    label: 'Customer full name',
    example: 'Jane Smith',
    source: 'customer',
  },

  // system
  {
    path: 'system.companyName',
    label: 'Our company name',
    example: 'Tech Solvers',
    source: 'system',
  },
  {
    path: 'system.today',
    label: "Today's date",
    example: '19 May 2026',
    source: 'system',
  },

  // quote — added with markdown support. Every money value below is the
  // CUSTOMER-FACING figure: bundle discount and diagnostics credit deducted,
  // EFT buffer applied, in the quote's own price display mode. That is the
  // number on the quote's Total row, which is the only number terms text may
  // quote back at the customer without contradicting the quote itself.
  {
    path: 'quote.name',
    label: 'Quote title',
    example: 'Reception PC replacement',
    source: 'quote',
  },
  {
    path: 'quote.type',
    label: 'Quote type',
    example: 'New Laptop',
    source: 'quote',
  },
  {
    path: 'quote.leadTime',
    label: 'Longest lead time',
    example: '5-7 business days',
    source: 'quote',
  },
  {
    path: 'quote.jobNumber',
    label: 'Job / ticket number',
    example: '18422',
    source: 'quote',
  },
  {
    path: 'quote.subtotal',
    label: 'Subtotal (before discounts)',
    example: '$1,349.00',
    source: 'quote',
  },
  {
    path: 'quote.totalExGst',
    label: 'Total ex GST',
    example: '$1,122.33',
    source: 'quote',
  },
  {
    path: 'quote.gst',
    label: 'GST component',
    example: '$112.23',
    source: 'quote',
  },
  {
    path: 'quote.totalIncGst',
    label: 'Total inc GST',
    example: '$1,234.56',
    source: 'quote',
  },

  // customer — contact channels, so terms can name where to reply.
  {
    path: 'customer.email',
    label: 'Customer email',
    example: 'jane@acme.com.au',
    source: 'customer',
  },
  {
    path: 'customer.phone',
    label: 'Customer phone',
    example: '(02) 9000 1234',
    source: 'customer',
  },
  {
    path: 'customer.mobile',
    label: 'Customer mobile',
    example: '0412 345 678',
    source: 'customer',
  },
];
